// DocumentsDock — the floating DOCUMENTS tab on the right edge of every
// record page, the sibling of the Notes dock at the bottom (X1, Grant
// 2026-09-04: "a similar floating documents feature… on the right side…
// opens from the right on top of things").
//
// Same three departures the Notes dock made from Ask Stew: NOT modal (no
// scrim, no focus trap — you keep using the record behind it), docked to
// the viewport rather than mounted inside any page, and keyed on the
// record so nothing survives onto another household. And one rule of its
// own, shared with the Notes dock through dock-state: only one of the two
// is open at a time; the other's tab shrinks and goes inert.
//
// The panel body IS the record's Documents tab — the same component, the
// same list / preview / rename / move / upload, the same permissions —
// so nothing becomes reachable here that the tab did not already allow.
//
// CONSUMER: internal-ui.

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FolderOpen, X } from "lucide-react";

import { api } from "../../api";
import type { NoteEntitySegment } from "@shared/types/notes";
import { DocumentsTab } from "../DocumentsTab";
import type { DocumentsRouteEntityType } from "./documents-dock-route";
import { useDock } from "../docks/dock-state";

const LABEL: Record<NoteEntitySegment, string> = {
  households: "this household",
  contacts: "this person",
  "service-records": "this record",
  opportunities: "this opportunity",
  transactions: "this processing record",
  "rhythm-instances": "this household (via its care plan)",
};

export interface DocumentsDockProps {
  entityType: DocumentsRouteEntityType | "household-of-rhythm";
  entityId: string;
  kind: NoteEntitySegment;
}

export function DocumentsDock({ entityType, entityId, kind }: DocumentsDockProps) {
  const { isOpen, otherOpen, setOpen } = useDock("documents");
  // Ongoing Care: resolve the cycle's household once, only when opened.
  const [resolved, setResolved] = useState<{ type: DocumentsRouteEntityType; id: string } | null>(
    entityType === "household-of-rhythm" ? null : { type: entityType, id: entityId },
  );
  const [resolveError, setResolveError] = useState<string | null>(null);

  useEffect(() => {
    setOpen(false);
    setResolved(entityType === "household-of-rhythm" ? null : { type: entityType, id: entityId });
    setResolveError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on record change only
  }, [entityType, entityId]);

  useEffect(() => {
    if (!isOpen || resolved || entityType !== "household-of-rhythm") return;
    let cancelled = false;
    api
      .getRhythmInstanceDetail(entityId)
      .then((r) => {
        if (!cancelled) setResolved({ type: "household", id: r.rhythm.household_id });
      })
      .catch(() => {
        if (!cancelled) setResolveError("Couldn't find this plan's household.");
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, resolved, entityType, entityId]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, setOpen]);

  if (typeof document === "undefined") return null;
  const label = LABEL[kind];

  if (!isOpen) {
    return createPortal(
      <button
        type="button"
        className={`documents-dock-tab${otherOpen ? " documents-dock-tab--min" : ""}`}
        onClick={() => {
          if (!otherOpen) setOpen(true);
        }}
        aria-disabled={otherOpen || undefined}
        title={otherOpen ? "Close Notes first" : "Documents"}
        data-testid="documents-dock-tab"
      >
        <FolderOpen size={15} strokeWidth={1.6} aria-hidden />
        <span>Documents</span>
      </button>,
      document.body,
    );
  }

  return createPortal(
    <section
      className="documents-dock"
      aria-label={`Documents on ${label}`}
      data-testid="documents-dock-panel"
    >
      <header className="documents-dock-header">
        <h2 className="documents-dock-title">
          <FolderOpen size={15} strokeWidth={1.6} aria-hidden />
          Documents
          <span className="documents-dock-sub">on {label}</span>
        </h2>
        <button
          type="button"
          className="documents-dock-close"
          onClick={() => setOpen(false)}
          aria-label="Close documents"
          title="Close documents (Esc)"
        >
          <X size={16} strokeWidth={1.8} aria-hidden />
        </button>
      </header>
      <div className="documents-dock-body">
        {resolved ? (
          <DocumentsTab
            entityType={resolved.type}
            entityId={resolved.id}
            entityLabel={label}
            {...(resolved.type === "household" ? { householdId: resolved.id } : {})}
          />
        ) : resolveError ? (
          <div className="documents-dock-empty">{resolveError}</div>
        ) : (
          <div className="documents-dock-empty">Loading…</div>
        )}
      </div>
    </section>,
    document.body,
  );
}
